import {
  MasterDataCollectionRemoteCropMaterialization,
  MasterLabelStateSaveRequest,
  MasterUPCData,
  RemoteDataCollectionDetection,
} from '../types';
import { collectUpcCrops } from './masterLabelCrops';

const REMOTE_TEMP_CROP_PREFIX = 'remote-dc:';

export function remoteTempCropId(detectionId: string): string {
  return `${REMOTE_TEMP_CROP_PREFIX}${detectionId}`;
}

export function isRemoteTempCropId(cropId: string): boolean {
  return cropId.startsWith(REMOTE_TEMP_CROP_PREFIX);
}

export function remoteDetectionIdFromTempCropId(cropId: string): string | null {
  return isRemoteTempCropId(cropId) ? cropId.slice(REMOTE_TEMP_CROP_PREFIX.length) : null;
}

export function prepareRemoteCropMaterializations(
  state: MasterLabelStateSaveRequest,
  upc: MasterUPCData,
  detections: RemoteDataCollectionDetection[]
): {
  remoteCrops: MasterDataCollectionRemoteCropMaterialization[];
  submittedDetectionIds: string[];
  missingTempCropIds: string[];
} {
  const placedTempCropIds = new Set<string>();
  state.items.forEach((item) => {
    item.designs.forEach((design) => {
      design.cropIds.forEach((cropId) => {
        if (isRemoteTempCropId(cropId)) placedTempCropIds.add(cropId);
      });
    });
    [
      item.pendingCropIds,
      item.unassignedCropIds,
      item.cartonCropIds,
      item.inactiveCropIds,
      item.differentItemCropIds ?? [],
    ].forEach((cropIds) => {
      cropIds.forEach((cropId) => {
        if (isRemoteTempCropId(cropId)) placedTempCropIds.add(cropId);
      });
    });
  });

  const currentCropsById = new Map(collectUpcCrops(upc).map((crop) => [crop.id, crop]));
  const detectionTempCropIds = new Set(
    detections.map((detection) => remoteTempCropId(detection.detectionId))
  );
  const remoteCrops = detections.flatMap((detection) => {
    const tempCropId = remoteTempCropId(detection.detectionId);
    if (!placedTempCropIds.has(tempCropId)) return [];
    const crop = currentCropsById.get(tempCropId);
    return [
      {
        temp_crop_id: tempCropId,
        shape_list: detection.shapeList,
        index: detection.index,
        orientation: crop?.orientation === 'not-front' ? 'not-front' : 'front',
        classification_type: crop?.classificationType === 'carton' ? 'carton' : 'product',
      } satisfies MasterDataCollectionRemoteCropMaterialization,
    ];
  });

  return {
    remoteCrops,
    submittedDetectionIds: remoteCrops
      .map((crop) => remoteDetectionIdFromTempCropId(crop.temp_crop_id))
      .filter((detectionId): detectionId is string => Boolean(detectionId)),
    missingTempCropIds: [...placedTempCropIds]
      .filter((cropId) => !detectionTempCropIds.has(cropId))
      .sort(),
  };
}
